import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Product } from '~shared/models';
import { CarouselComponent } from 'ngx-owl-carousel-o';
import { Observable, Subject } from 'rxjs';
import { BlackDealsFeatureFlagService } from '~shared/services/black-deals-feature-flag.service';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FeatureToggleEnum } from '~shared/constants';
import { VariantType, VariantTypeObject } from '~shared/models/product-card/product-variant.model';
import { OwlSingeDot } from 'ngx-owl-carousel-o/lib/models/navigation-data.models';
import { PlpAddToCartService } from '~shared/modules/products-grid/services/plp-add-to-cart.service';
import { takeUntil } from 'rxjs/operators';
import { AddToCardConfig } from '~shared/modules/product-card/models/add-to-card-config.model';

const PRODUCT_CAROUSEL_OPTIONS = {
  mouseDrag: false,
  touchDrag: true,
  pullDrag: false,
  dots: true,
  navSpeed: 400,
  autoplay: false,
  arrows: false,
  nav: false,
  responsiveRefreshRate: 200,
  //stagePadding: 80,
  slideBy: 'page',
  slideTransition: 'ease-out',
  responsive: {
    0: {
      items: 3,
      slideBy: 3,
    },
    1024: {
      items: 5,
      slideBy: 5,
    },
    1440: {
      items: 6,
      slideBy: 6,
    },
  },
};

@Component({
  selector: 'app-product-carousel',
  templateUrl: './app-product-carousel.component.html',
  styleUrls: ['./app-product-carousel.component.scss'],
})
export class AppProductCarouselComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() products: Product[];
  @Input() isMobile: boolean;
  @Input() isDesktop: boolean;
  @Input() noBorder = false;
  @Input() isSimilarProductRecommendation: boolean;

  @Output() productCarouselPrev = new EventEmitter();
  @Output() productCarouselNext = new EventEmitter();
  @Output() swipeLeft = new EventEmitter();
  @Output() swipeRight = new EventEmitter();
  @Output() productClicked = new EventEmitter<{ product: Product; priceType: PriceTypes; index: number }>();

  @ViewChild('productCarousel') productCarousel: CarouselComponent;
  carouselOverflow = 'overflowRight';
  customCarouselOptions = PRODUCT_CAROUSEL_OPTIONS;
  carouselPages: number;
  carouselCurrentPage: number;
  showRightArrow = true;
  isBlackDealsEnabled$: Observable<boolean>;
  isVariantsRedesignEnabled$: Observable<boolean>;
  public addToCartConfig: AddToCardConfig;
  private destroySubject$ = new Subject<boolean>();

  constructor(
    private blackDealsFeatureFlagService: BlackDealsFeatureFlagService,
    private cd: ChangeDetectorRef,
    private activeFeaturesConfigcatService: ActiveFeaturesConfigcatService,
    private plpAddToCartService: PlpAddToCartService
  ) {}

  ngOnInit(): void {
    this.isBlackDealsEnabled$ = this.blackDealsFeatureFlagService.isEnabled();
    this.isVariantsRedesignEnabled$ = this.activeFeaturesConfigcatService.isFeatureEnabled(
      FeatureToggleEnum.PRODUCT_CARDS_CAROUSELS_RESTYLE
    );
    this.plpAddToCartService
      .getPlpsAddToCartVariant()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((addToCartConfig: AddToCardConfig) => {
        this.addToCartConfig = addToCartConfig;
      });
  }

  ngAfterViewInit() {
    // this trick fixes disappearing arrows,
    // in fact productCarousel.slidesData.length is not reflected in template
    this.cd.detectChanges();
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  public someProductHasVariants(): { hasSizes: boolean; hasColors: boolean } {
    const hasSizes: boolean = this.products?.some((product: Product): boolean => {
      const variantTypeObject: VariantTypeObject = product?.variant?.types;
      const variantTypes: [string, VariantType][] = variantTypeObject ? Object.entries(variantTypeObject) : [];
      const notColorVariants: [string, VariantType][] = variantTypes?.filter(
        (variant: [string, VariantType]) => variant[0] !== 'color'
      );
      return notColorVariants && !!notColorVariants?.length;
    });
    const hasColors: boolean = this.products?.some((product: Product): boolean => {
      const variantTypeObject: VariantTypeObject = product?.variant?.types;
      const variantTypes: [string, VariantType][] = variantTypeObject ? Object.entries(variantTypeObject) : [];
      const colorVariants: [string, VariantType][] = variantTypes?.filter(
        (variant: [string, VariantType]): boolean => variant[0] === 'color'
      );

      return colorVariants && !!colorVariants?.length;
    });

    return {
      hasSizes,
      hasColors,
    };
  }

  someProductsHasTotalUnitsOrBasePrice() {
    return this.products?.some((product: Product) => {
      return product.bestOffer?.unitPrice?.unitCount > 1 || product?.bestOffer?.basePrice;
    });
  }

  trackById(_, item: Product) {
    return item.id;
  }

  onCarouselMove() {
    const productCarouselDots: OwlSingeDot[] = this.productCarousel?.dotsData?.dots || [];

    this.carouselPages = productCarouselDots.length;
    this.carouselCurrentPage = productCarouselDots.findIndex((dot) => dot.active) + 1;

    if (this.carouselCurrentPage === 1) {
      this.carouselOverflow = 'overflowRight';
      this.showRightArrow = true;
    }

    if (this.carouselCurrentPage === this.carouselPages) {
      this.carouselOverflow = 'overflowLeft';
      this.showRightArrow = false;
    }

    if (
      (this.carouselCurrentPage !== 1 && this.carouselCurrentPage !== this.carouselPages) ||
      (this.carouselCurrentPage === 1 && !this.productCarousel.slidesData[0].isActive)
    ) {
      this.carouselOverflow = '';
      this.showRightArrow = true;
    }
  }

  onProductClick(product: Product, priceType: PriceTypes, index: number): void {
    this.productClicked.emit({
      product,
      priceType,
      index,
    });
  }

  onProductCarouselPrev(): void {
    this.productCarouselPrev.emit();
  }

  onProductCarouselNext(): void {
    this.productCarouselNext.emit();
  }

  trackLeftSwipe(): void {
    this.swipeLeft.emit();
  }

  trackRightSwipe(): void {
    this.swipeRight.emit();
  }
}
